import React from 'react';
import { motion } from 'framer-motion';

/* ─────────────────────────────────────────────────────────────
   Gradient map — one per effective mood
───────────────────────────────────────────────────────────── */
const GRADIENTS = {
  sunrise:     'from-[#ff9a3c] via-[#ffcc70] to-[#87ceeb]',
  afternoon:   'from-[#0ea5e9] via-[#38bdf8] to-[#7dd3fc]',
  sunset:      'from-[#1a0533] via-[#7c2d12] to-[#ea580c]',
  night:       'from-[#020617] via-[#0f172a] to-[#1e1b4b]',
  clouds:      'from-[#374151] via-[#4b5563] to-[#6b7280]',
  rain:        'from-[#0f172a] via-[#1e293b] to-[#263347]',
  drizzle:     'from-[#1e293b] via-[#334155] to-[#3f5068]',
  thunderstorm:'from-[#050511] via-[#0f0f1a] to-[#1a1a2e]',
  snow:        'from-[#c7d9ec] via-[#dde8f4] to-[#f0f6fb]',
  mist:        'from-[#334155] via-[#475569] to-[#64748b]',
  haze:        'from-[#78716c] via-[#a8a29e] to-[#d6d3d1]',
  fog:         'from-[#334155] via-[#475569] to-[#64748b]',
  wind:        'from-[#334155] via-[#475569] to-[#64748b]',
};

const WeatherVisuals = ({ mood }) => {
  const gradient = GRADIENTS[mood] || GRADIENTS.afternoon;

  return (
    <div className={`fixed inset-0 transition-all duration-1000 bg-gradient-to-b ${gradient}`}>
      <div className="absolute inset-0 overflow-hidden">
        {mood === 'sunrise'      && <SunriseEffect />}
        {mood === 'afternoon'    && <AfternoonEffect />}
        {mood === 'sunset'       && <SunsetEffect />}
        {mood === 'night'        && <NightEffect />}
        {(mood === 'rain' || mood === 'drizzle') && <RainEffect />}
        {mood === 'thunderstorm' && <ThunderstormEffect />}
        {mood === 'snow'         && <SnowEffect />}
        {mood === 'clouds'       && <CloudyEffect />}
        {(mood === 'mist' || mood === 'haze' || mood === 'fog' || mood === 'wind') && <MistEffect />}
      </div>
      {/* Global subtle vignette for depth */}
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.25) 100%)' }} />
    </div>
  );
};

/* ─── SUNRISE ────────────────────────────────────────────────
   Warm golden light, sun cresting the horizon, soft haze     */
const SunriseEffect = () => (
  <>
    {/* Horizon glow */}
    <div className="absolute bottom-0 left-0 w-full h-1/2"
      style={{ background: 'linear-gradient(to top, rgba(255,165,50,0.6), transparent)', filter: 'blur(30px)' }} />

    {/* Sun disk rising from bottom-right */}
    <motion.div
      animate={{ y: [60, 0, 60], scale: [0.95, 1.05, 0.95] }}
      transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
      className="absolute bottom-[5%] right-[10%] w-56 h-56 rounded-full"
      style={{
        background: 'radial-gradient(circle, #fff7e6 0%, #ffd700 40%, #ff8c00 70%, transparent 100%)',
        boxShadow: '0 0 120px 60px rgba(255,160,0,0.5)',
        filter: 'blur(2px)',
      }}
    />

    {/* Long horizontal dawn rays */}
    <motion.div
      animate={{ opacity: [0.3, 0.6, 0.3] }}
      transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      className="absolute bottom-[15%] right-0 w-full h-[2px]"
      style={{ background: 'linear-gradient(to left, rgba(255,200,50,0.8), transparent)' }}
    />
    <motion.div
      animate={{ opacity: [0.2, 0.5, 0.2] }}
      transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
      className="absolute bottom-[18%] right-0 w-3/4 h-[1px]"
      style={{ background: 'linear-gradient(to left, rgba(255,160,50,0.6), transparent)' }}
    />
    <motion.div
      animate={{ opacity: [0.1, 0.4, 0.1] }}
      transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
      className="absolute bottom-[22%] right-0 w-1/2 h-[1px]"
      style={{ background: 'linear-gradient(to left, rgba(255,120,50,0.5), transparent)' }}
    />

    {/* Silhouette atmospheric haze layer */}
    <div className="absolute bottom-0 left-0 w-full h-32"
      style={{ background: 'linear-gradient(to top, rgba(255,140,0,0.2), transparent)', filter: 'blur(20px)' }} />

    {/* Pink-purple atmospheric scatter */}
    <motion.div
      animate={{ opacity: [0.2, 0.4, 0.2] }}
      transition={{ duration: 5, repeat: Infinity }}
      className="absolute top-0 left-0 w-2/3 h-1/3 rounded-br-full"
      style={{ background: 'radial-gradient(ellipse, rgba(180,120,200,0.3), transparent)', filter: 'blur(40px)' }}
    />
  </>
);

/* ─── AFTERNOON ──────────────────────────────────────────────
   Blazing sun, vivid blue sky, intense heat atmosphere        */
const AfternoonEffect = () => (
  <>
    {/* Blazing sun at top-right */}
    <div className="absolute -top-20 -right-20 w-96 h-96">
      {/* Outer glow */}
      <div className="absolute inset-0 rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(255,255,200,0.5) 0%, rgba(255,220,50,0.3) 50%, transparent 80%)', filter: 'blur(30px)' }} />
      {/* Sun disk */}
      <motion.div
        animate={{ scale: [1, 1.04, 1], opacity: [0.9, 1, 0.9] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute top-12 right-12 w-48 h-48 rounded-full"
        style={{
          background: 'radial-gradient(circle, #fffde7 0%, #ffe082 40%, #ffb300 75%, transparent 100%)',
          boxShadow: '0 0 80px 40px rgba(255,210,0,0.4)',
        }}
      />
    </div>

    {/* Rotating sun rays */}
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
      className="absolute -top-32 -right-32 w-[700px] h-[700px]"
      style={{ transformOrigin: 'center', opacity: 0.35 }}
    >
      {[...Array(16)].map((_, i) => (
        <div key={i}
          className="absolute left-1/2 top-1/2 w-0.5 origin-bottom"
          style={{
            height: '320px',
            background: 'linear-gradient(to top, rgba(255,230,50,0.8), transparent)',
            transform: `rotate(${i * 22.5}deg) translateX(-50%)`,
          }}
        />
      ))}
    </motion.div>

    {/* Heat shimmer at the bottom */}
    <motion.div
      animate={{ scaleY: [1, 1.05, 1], opacity: [0.1, 0.18, 0.1] }}
      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      className="absolute bottom-0 left-0 w-full h-40"
      style={{ background: 'linear-gradient(to top, rgba(255,200,50,0.2), transparent)', filter: 'blur(20px)' }}
    />

    {/* Ambient blue sky atmosphere */}
    <div className="absolute top-0 left-0 w-full h-full pointer-events-none"
      style={{ background: 'radial-gradient(ellipse at 70% 0%, rgba(255,255,255,0.1) 0%, transparent 60%)' }} />
  </>
);

/* ─── SUNSET ─────────────────────────────────────────────────
   Deep orange-purple sky, large sun on horizon, crepuscular rays */
const SunsetEffect = () => (
  <>
    {/* Deep horizon glow */}
    <motion.div
      animate={{ opacity: [0.7, 1, 0.7] }}
      transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      className="absolute bottom-0 right-0 w-full h-2/3"
      style={{ background: 'linear-gradient(to top, rgba(234,88,12,0.7) 0%, rgba(124,45,18,0.5) 40%, transparent 80%)', filter: 'blur(20px)' }}
    />

    {/* Large setting sun near horizon */}
    <motion.div
      animate={{ y: [0, 10, 0], scale: [1, 1.03, 1] }}
      transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
      className="absolute bottom-[8%] right-[8%] w-72 h-72 rounded-full"
      style={{
        background: 'radial-gradient(circle, #fff3cd 0%, #ff6b35 50%, #c2410c 75%, transparent 100%)',
        boxShadow: '0 0 150px 80px rgba(234,88,12,0.45)',
        filter: 'blur(3px)',
      }}
    />

    {/* Fan of crepuscular rays */}
    {[...Array(8)].map((_, i) => (
      <motion.div
        key={i}
        animate={{ opacity: [0.1, 0.35, 0.1] }}
        transition={{ duration: 5 + i * 0.5, repeat: Infinity, delay: i * 0.3 }}
        className="absolute bottom-[10%] right-[18%] origin-bottom-right"
        style={{
          width: '3px',
          height: '70vh',
          background: 'linear-gradient(to top, rgba(255,140,0,0.6), transparent)',
          transform: `rotate(${-40 + i * 12}deg)`,
          filter: 'blur(4px)',
        }}
      />
    ))}

    {/* Purple atmospheric upper sky */}
    <motion.div
      animate={{ opacity: [0.4, 0.6, 0.4] }}
      transition={{ duration: 7, repeat: Infinity }}
      className="absolute top-0 left-0 w-full h-1/2"
      style={{ background: 'radial-gradient(ellipse at 30% 0%, rgba(139,92,246,0.3), transparent 70%)', filter: 'blur(60px)' }}
    />
    {/* Pink band */}
    <div className="absolute top-1/3 left-0 w-full h-24"
      style={{ background: 'linear-gradient(to right, transparent, rgba(244,114,182,0.25), transparent)', filter: 'blur(30px)' }} />
  </>
);

/* ─── NIGHT ─────────────────────────────────────────────────── */
const NightEffect = () => (
  <>
    {/* Moon */}
    <motion.div
      animate={{ opacity: [0.7, 1, 0.7], scale: [1, 1.02, 1] }}
      transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      className="absolute top-16 right-24 w-28 h-28 rounded-full bg-gradient-to-br from-zinc-100 to-zinc-300"
      style={{ boxShadow: '0 0 80px rgba(255,255,255,0.2)', filter: 'blur(1px)' }}
    />
    <div className="absolute top-4 right-12 w-56 h-56 rounded-full bg-white/5" style={{ filter: 'blur(50px)' }} />
    {/* Stars */}
    {[...Array(100)].map((_, i) => (
      <motion.div key={i}
        animate={{ opacity: [Math.random() * 0.2, 0.9, Math.random() * 0.2] }}
        transition={{ duration: 2 + Math.random() * 4, repeat: Infinity, delay: Math.random() * 6 }}
        className="absolute rounded-full bg-white"
        style={{ width: Math.random() * 2.5 + 'px', height: Math.random() * 2.5 + 'px', top: Math.random() * 90 + '%', left: Math.random() * 100 + '%' }}
      />
    ))}
    {/* Milky way band */}
    <div className="absolute top-0 left-1/4 w-1/2 h-full pointer-events-none"
      style={{ background: 'linear-gradient(160deg, transparent 0%, rgba(255,255,255,0.02) 50%, transparent 100%)', filter: 'blur(20px)' }} />
  </>
);

/* ─── RAIN ───────────────────────────────────────────────────── */
const RainEffect = () => (
  <>
    <div className="absolute -top-20 -left-20 w-full h-64 rounded-full bg-slate-700/40" style={{ filter: 'blur(50px)' }} />
    {[...Array(80)].map((_, i) => (
      <motion.div key={i}
        initial={{ y: '-10vh', opacity: 0 }}
        animate={{ y: '110vh', opacity: [0, 0.6, 0.6, 0] }}
        transition={{ duration: 0.5 + Math.random() * 0.4, repeat: Infinity, delay: Math.random() * 2, ease: 'linear' }}
        className="absolute top-0"
        style={{ left: Math.random() * 100 + '%', width: '1.5px', height: 40 + Math.random() * 30 + 'px', background: 'linear-gradient(to bottom, transparent, rgba(147,197,253,0.7))' }}
      />
    ))}
    <motion.div animate={{ opacity: [0.1, 0.2, 0.1] }} transition={{ duration: 3, repeat: Infinity }}
      className="absolute bottom-0 left-0 w-full h-32 bg-blue-400/10" style={{ filter: 'blur(30px)' }} />
  </>
);

/* ─── THUNDERSTORM ───────────────────────────────────────────── */
const ThunderstormEffect = () => {
  const [flash, setFlash] = React.useState(false);
  React.useEffect(() => {
    const fire = () => {
      setFlash(true);
      setTimeout(() => setFlash(false), 100);
      setTimeout(() => { setFlash(true); setTimeout(() => setFlash(false), 80); }, 150);
    };
    const interval = setInterval(fire, 4000 + Math.random() * 5000);
    return () => clearInterval(interval);
  }, []);
  return (
    <>
      <RainEffect />
      {flash && <div className="absolute inset-0 bg-white/25 pointer-events-none z-50" />}
      {flash && (
        <svg className="absolute top-10 left-1/3 w-16 h-56 text-yellow-100 opacity-90 z-50" viewBox="0 0 24 64" fill="currentColor">
          <polygon points="14,0 4,36 11,36 7,64 22,24 14,24" />
        </svg>
      )}
    </>
  );
};

/* ─── SNOW ───────────────────────────────────────────────────── */
const SnowEffect = () => (
  <>
    <div className="absolute inset-0 bg-white/5" style={{ filter: 'blur(40px)' }} />
    {[...Array(70)].map((_, i) => {
      const size = Math.random() * 7 + 3;
      return (
        <motion.div key={i}
          animate={{ y: ['0vh', '105vh'], x: [0, (Math.random() - 0.5) * 120], rotate: [0, 360] }}
          transition={{ duration: 5 + Math.random() * 8, repeat: Infinity, ease: 'linear', delay: -Math.random() * 12 }}
          className="absolute rounded-full bg-white"
          style={{ left: Math.random() * 100 + '%', top: '-20px', width: size + 'px', height: size + 'px', opacity: 0.6 + Math.random() * 0.4 }}
        />
      );
    })}
    <div className="absolute bottom-0 left-0 w-full h-20 bg-white/20" style={{ filter: 'blur(20px)' }} />
  </>
);

/* ─── CLOUDY ─────────────────────────────────────────────────── */
const CloudyEffect = () => (
  <div className="absolute inset-0 overflow-hidden">
    {[...Array(10)].map((_, i) => (
      <motion.div key={i}
        animate={{ x: ['-20%', '120%'] }}
        transition={{ duration: 35 + Math.random() * 45, repeat: Infinity, ease: 'linear', delay: -Math.random() * 50 }}
        className="absolute bg-white/10 blur-[25px] rounded-full"
        style={{ width: 250 + Math.random() * 350 + 'px', height: 120 + Math.random() * 120 + 'px', top: Math.random() * 80 + '%' }}
      />
    ))}
    {[...Array(6)].map((_, i) => (
      <motion.div key={`dark-${i}`}
        animate={{ x: ['120%', '-20%'] }}
        transition={{ duration: 70 + Math.random() * 50, repeat: Infinity, ease: 'linear', delay: -Math.random() * 70 }}
        className="absolute bg-slate-600/10 blur-[35px] rounded-full"
        style={{ width: 400 + Math.random() * 400 + 'px', height: 200 + Math.random() * 100 + 'px', top: 5 + Math.random() * 60 + '%' }}
      />
    ))}
  </div>
);

/* ─── MIST / HAZE / FOG / WIND ────────────────────────────────── */
const MistEffect = () => (
  <>
    {[...Array(20)].map((_, i) => (
      <motion.div key={i}
        initial={{ x: '-300px', opacity: 0 }}
        animate={{ x: '110vw', opacity: [0, 0.4, 0.4, 0] }}
        transition={{ duration: 2 + Math.random() * 2.5, repeat: Infinity, delay: Math.random() * 4, ease: 'linear' }}
        className="absolute bg-white/40 rounded-full"
        style={{ top: Math.random() * 90 + '%', height: 1 + Math.random() * 2 + 'px', width: 80 + Math.random() * 200 + 'px', filter: 'blur(1px)' }}
      />
    ))}
    {[...Array(5)].map((_, i) => (
      <motion.div key={`m-${i}`}
        animate={{ x: ['-5%', '5%', '-5%'], opacity: [0.1, 0.25, 0.1] }}
        transition={{ duration: 9 + i * 2, repeat: Infinity, ease: 'easeInOut', delay: i * 1.5 }}
        className="absolute bg-white/20 rounded-full"
        style={{ width: 600 + i * 100 + 'px', height: '150px', top: 20 + i * 15 + '%', left: '-10%', filter: 'blur(60px)' }}
      />
    ))}
  </>
);

export default WeatherVisuals;
